import { http } from "@/shared/lib/api/http"
import {
  type CreateMinistryTypeInput,
  MinistryStatus,
  type MinistryType,
  type MinistryTypesListQuery,
  type MinistryTypesListResponse,
  type UpdateMinistryTypeInput,
} from "@/features/church/config/ministries/types/ministry.types"

/** Raw `/ministry-types` payload (camelCase envelope; see ministry-types.routes.ts). */
type MinistryTypeApiResponse = {
  id: string
  workspaceId: string
  name: string
  description?: string | null
  systemKey?: string | null
  isSystem?: boolean | null
  status?: string | null
  ministersCount?: number | null
  createdAt?: string
  updatedAt?: string
}

type MinistryTypeListApiResponse = {
  items?: MinistryTypeApiResponse[]
  total?: number
  page?: number
  limit?: number
}

function mapStatus(value: unknown): MinistryStatus {
  return value === "INACTIVE" ? MinistryStatus.INACTIVE : MinistryStatus.ACTIVE
}

function normalizeMinistryType(input: MinistryTypeApiResponse): MinistryType {
  return {
    id: input.id,
    workspaceId: input.workspaceId,
    name: input.name,
    description: input.description ?? null,
    systemKey: input.systemKey ?? null,
    isSystem: Boolean(input.isSystem),
    status: mapStatus(input.status),
    ministersCount: input.ministersCount ?? 0,
    createdAt: input.createdAt ?? new Date().toISOString(),
    updatedAt: input.updatedAt ?? input.createdAt ?? new Date().toISOString(),
  }
}

function buildListParams(query: MinistryTypesListQuery) {
  const params: Record<string, string | number> = {}

  if (query.workspaceId) params.workspaceId = query.workspaceId
  if (query.status) params.status = query.status
  const search = (query.search ?? query.q ?? "").trim()
  if (search) params.search = search
  if (typeof query.page === "number" && query.page > 0) params.page = query.page
  if (typeof query.limit === "number" && query.limit > 0) params.limit = query.limit
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortOrder) params.sortOrder = query.sortOrder
  if (query.filters && query.filters.trim()) params.filters = query.filters.trim()

  return params
}

function toNullableString(value: string | null | undefined) {
  if (value === null || value === undefined) return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export async function listMinistryTypesService(
  query: MinistryTypesListQuery
): Promise<MinistryTypesListResponse> {
  const response = await http.get<MinistryTypeListApiResponse>("/ministry-types", {
    params: buildListParams(query),
  })

  const items = Array.isArray(response.data.items) ? response.data.items.map(normalizeMinistryType) : []
  return {
    items,
    total: typeof response.data.total === "number" ? response.data.total : items.length,
    page: typeof response.data.page === "number" && response.data.page > 0 ? response.data.page : 1,
    limit:
      typeof response.data.limit === "number" && response.data.limit > 0
        ? response.data.limit
        : Math.max(items.length, 1),
  }
}

export async function getMinistryTypeService(id: string): Promise<MinistryType> {
  const response = await http.get<MinistryTypeApiResponse>(`/ministry-types/${id}`)
  return normalizeMinistryType(response.data)
}

export async function createMinistryTypeService(input: CreateMinistryTypeInput): Promise<MinistryType> {
  const payload: Record<string, unknown> = {
    name: input.name.trim(),
  }
  if (input.workspaceId !== undefined && input.workspaceId !== null) {
    payload.workspaceId = input.workspaceId
  }
  if (input.description !== undefined) {
    payload.description = toNullableString(input.description)
  }
  if (input.status !== undefined) {
    payload.status = input.status
  }

  const response = await http.post<MinistryTypeApiResponse>("/ministry-types", payload)
  return normalizeMinistryType(response.data)
}

export async function updateMinistryTypeService(
  id: string,
  input: UpdateMinistryTypeInput
): Promise<MinistryType> {
  const payload: Record<string, unknown> = {}
  if (input.name !== undefined) {
    payload.name = input.name.trim()
  }
  if (input.description !== undefined) {
    payload.description = toNullableString(input.description)
  }
  if (input.status !== undefined) {
    payload.status = input.status
  }

  const response = await http.patch<MinistryTypeApiResponse>(`/ministry-types/${id}`, payload)
  return normalizeMinistryType(response.data)
}

export async function deleteMinistryTypeService(id: string): Promise<void> {
  await http.delete(`/ministry-types/${id}`)
}
